import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Container } from '../../components/layout/Container';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { useApplyForAgent, useMyAgentProfile } from '../../hooks/useAgent';
import { Spinner } from '../../components/common/Spinner';
import { Store, Mail, Phone, Globe, MapPin, Briefcase } from 'lucide-react';

type FormData = {
  storeAddress: string;
  storePrimaryActivity: string;
  storeContactEmail: string;
  storeContactPhone: string;
  businessName?: string;
  website?: string;
};

export const AgentApply: React.FC = () => {
  const navigate = useNavigate();
  const { data: profileData, isLoading: profileLoading } = useMyAgentProfile();
  const apply = useApplyForAgent();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>();

  useEffect(() => {
    if (profileData?.data) {
      if (profileData.data.status !== 'REJECTED') {
        navigate('/agent/status', { replace: true });
      } else {
        // Pre-fill form with previous application data
        reset({
          storeAddress: profileData.data.storeAddress || '',
          storePrimaryActivity: profileData.data.storePrimaryActivity || '',
          storeContactEmail: profileData.data.storeContactEmail || '',
          storeContactPhone: profileData.data.storeContactPhone || '',
          businessName: profileData.data.businessName || '',
          website: profileData.data.website || '',
        });
      }
    }
  }, [profileData, navigate, reset]);

  const onSubmit = async (data: FormData) => {
    await apply.mutateAsync(data);
    navigate('/agent/status');
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-12"><Spinner size="lg" /></Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-10 max-w-2xl">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 dark:bg-indigo-900/50 rounded-2xl mb-4">
            <Store className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Become a RaffleBowl Agent</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">Sell tickets on our behalf and earn commission on every sale.</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Briefcase className="inline w-3.5 h-3.5 mr-1" />Business Name (optional)
              </label>
              <input {...register('businessName')} placeholder="Your business or store name"
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <MapPin className="inline w-3.5 h-3.5 mr-1" />Physical Store Address *
              </label>
              <textarea {...register('storeAddress', { required: 'Store address is required' })} rows={3}
                placeholder="Full physical address of your store"
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
              {errors.storeAddress && <p className="text-red-500 text-xs mt-1">{errors.storeAddress.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Briefcase className="inline w-3.5 h-3.5 mr-1" />Primary Store Activity *
              </label>
              <input {...register('storePrimaryActivity', { required: 'Primary activity is required' })}
                placeholder="e.g. Convenience Store, Pharmacy, Lottery Shop"
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              {errors.storePrimaryActivity && <p className="text-red-500 text-xs mt-1">{errors.storePrimaryActivity.message}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  <Mail className="inline w-3.5 h-3.5 mr-1" />Store Contact Email *
                </label>
                <input {...register('storeContactEmail', { required: 'Contact email is required' })} type="email"
                  placeholder="store@example.com"
                  className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                {errors.storeContactEmail && <p className="text-red-500 text-xs mt-1">{errors.storeContactEmail.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  <Phone className="inline w-3.5 h-3.5 mr-1" />Store Contact Phone *
                </label>
                <input {...register('storeContactPhone', { required: 'Contact phone is required' })}
                  placeholder="+1234567890"
                  className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                {errors.storeContactPhone && <p className="text-red-500 text-xs mt-1">{errors.storeContactPhone.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Globe className="inline w-3.5 h-3.5 mr-1" />Website (optional)
              </label>
              <input {...register('website')} placeholder="https://yourstore.com"
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div className="bg-indigo-50 dark:bg-indigo-950 rounded-xl p-4 text-sm text-indigo-700 dark:text-indigo-300">
              Your application will be reviewed by our team. You'll be notified once approved and will gain access to the agent dashboard.
            </div>

            <Button type="submit" fullWidth isLoading={apply.isPending}>Submit Application</Button>
          </form>
        </Card>
      </Container>
    </div>
  );
};
